import { useCallback, useEffect, useState } from "react";
import { fetchRfpFull } from "../services/rfpsService";
import type { RfpFullResponse } from "../types/rfp";

export type RfpFullReloadOptions = {
  /** Refresh data without setting `loading` (no full-page overlay). */
  readonly silent?: boolean;
};

export type UseRfpFullResult = {
  readonly rfp: RfpFullResponse | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly reload: (options?: RfpFullReloadOptions) => void;
};

export const useRfpFull = (rfpId: string | null): UseRfpFullResult => {
  const [rfp, setRfp] = useState<RfpFullResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchRequest, setFetchRequest] = useState({ generation: 0, silent: false });

  const reload = useCallback((options?: RfpFullReloadOptions) => {
    setFetchRequest((prev) => ({
      generation: prev.generation + 1,
      silent: options?.silent ?? false,
    }));
  }, []);

  useEffect(() => {
    const normalizedId = rfpId?.trim() ?? "";
    if (!normalizedId) {
      setRfp(null);
      setLoading(false);
      setError(null);
      setFetchRequest({ generation: 0, silent: false });
      return;
    }

    setFetchRequest({ generation: 0, silent: false });
    setRfp(null);
  }, [rfpId]);

  useEffect(() => {
    const normalizedId = rfpId?.trim() ?? "";
    if (!normalizedId) {
      return;
    }

    let cancelled = false;
    if (!fetchRequest.silent) {
      setLoading(true);
    }
    setError(null);

    void (async () => {
      try {
        const response = await fetchRfpFull(normalizedId);
        if (cancelled) return;
        setRfp(response);
      } catch (e) {
        if (cancelled) return;
        const message = e instanceof Error ? e.message : "Failed to load RFP details";
        setError(message);
        if (!fetchRequest.silent) {
          setRfp(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [rfpId, fetchRequest.generation, fetchRequest.silent]);

  return {
    rfp,
    loading,
    error,
    reload,
  };
};
